import { BranchSwitchedWidget } from "./BranchSwitchedWidget";
import { GitBranchWidget } from "./GitBranchWidget";
import { GitStatusWidget } from "./GitStatusWidget";
import { GitPushResultWidget } from "./GitPushResultWidget";
import { DiffViewerWidget } from "./DiffViewerWidget";
import { GitHubRepoAccessWidget } from "./GitHubRepoAccessWidget";
import { GitHubConnectWidget } from "./GitHubConnectWidget";
import { GitHubStatusWidget } from "./GitHubStatusWidget";
import { GitHubRepoPickerWidget } from "./GitHubRepoPickerWidget";
import { GitHubRepoConnectedWidget } from "./GitHubRepoConnectedWidget";
import { TaskListWidget } from "./TaskListWidget";
import { TaskAddedWidget } from "./TaskAddedWidget";
import { TaskUpdatedWidget } from "./TaskUpdatedWidget";
import { ApprovalWidget } from "./ApprovalWidget";
import { IntentConfirmWidget } from "./IntentConfirmWidget";
import { ClarifyWidget } from "./ClarifyWidget";
import { SvgBuilder } from "./SvgBuilder";
import { AnimationDesigner } from "./AnimationDesigner";
import { DeleteProjectWidget } from "./DeleteProjectWidget";
import { DeployConfirmWidget } from "./DeployConfirmWidget";
import { DeployProgressWidget } from "./DeployProgressWidget";
import { IterationWidget } from "./IterationWidget";
import { Card } from "@/components/ui/card";

interface WidgetAction {
  id: string;
  label: string;
  style?: string;
}

interface WidgetRendererProps {
  widgetType: string;
  data: Record<string, unknown>;
  actions?: WidgetAction[];
  onAction?: (actionId: string, context?: Record<string, unknown>) => void;
  onWidgetInputFocus?: (focused: boolean) => void;
}

/**
 * Routes widget payloads to the correct visual component.
 * Unknown widget types render a minimal fallback card.
 */
export function WidgetRenderer({ widgetType, data, actions, onAction, onWidgetInputFocus }: WidgetRendererProps) {
  switch (widgetType) {
    case "branch_switched":
      return <BranchSwitchedWidget data={data as any} />;

    case "git_branch":
      return <GitBranchWidget data={data as any} />;

    case "git_status":
      return (
        <GitStatusWidget
          data={data as any}
          actions={actions}
          onAction={onAction}
        />
      );

    case "git_push_result":
      return <GitPushResultWidget data={data as any} />;

    case "git_diff":
      return (
        <DiffViewerWidget
          data={data as any}
          actions={actions}
          onAction={onAction}
        />
      );

    case "github_repo_access":
      return (
        <GitHubRepoAccessWidget
          data={data as any}
          onAction={onAction}
        />
      );

    case "github_connect":
      return <GitHubConnectWidget />;

    case "github_status":
      return (
        <GitHubStatusWidget
          data={data as any}
          actions={actions}
          onAction={onAction}
        />
      );

    case "github_repo_picker":
      return (
        <GitHubRepoPickerWidget
          data={data as any}
        />
      );

    case "github_repo_connected":
      return <GitHubRepoConnectedWidget data={data as any} />;

    case "task_list":
      return <TaskListWidget data={data as any} onAction={onAction} />;

    case "task_added":
      return <TaskAddedWidget data={data as any} />;

    case "task_updated":
      return <TaskUpdatedWidget data={data as any} />;

    case "task_deleted":
      // Minimal confirmation — no dedicated component needed
      return (
        <Card variant="default" padding="sm" className="max-w-md animate-slide-up">
          <p className="text-sm text-daydra-dim">Task removed</p>
        </Card>
      );

    case "svg_builder":
      return <SvgBuilder data={data as any} onInputFocus={onWidgetInputFocus} />;

    case "animation_designer":
      return <AnimationDesigner data={data as any} onAction={onAction} onInputFocus={onWidgetInputFocus} />;

    case "approval_request":
      return <ApprovalWidget data={data as any} onAction={onAction} />;

    case "intent_confirm":
      return <IntentConfirmWidget data={data as any} onAction={onAction} />;

    case "clarify":
      return <ClarifyWidget data={data as any} onAction={onAction} />;

    case "delete_project":
      return <DeleteProjectWidget data={data as any} onAction={onAction} />;

    case "deploy_confirm":
      return <DeployConfirmWidget data={data as any} onAction={onAction} />;

    case "deploy_progress":
      return <DeployProgressWidget data={data as any} onAction={onAction} />;

    case "iteration":
      return <IterationWidget data={data} onAction={onAction} />;

    case "iteration_applied":
      // Handled as state update in ChatShell, not as a standalone widget.
      // If it somehow gets here, render nothing.
      return null;

    default:
      // Fallback for unknown widget types — render raw data
      return (
        <Card variant="default" padding="sm" className="max-w-md animate-slide-up">
          <p className="text-2xs text-daydra-muted font-mono">
            {widgetType}: {JSON.stringify(data, null, 2)}
          </p>
        </Card>
      );
  }
}
